const enabled = (value) => value === true || value === 1 || value === "1";

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const toNumber = (value) => {
  if (!hasValue(value)) {
    return null;
  }
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? null : numberValue;
};

const findLabel = (options = [], value, fallback = "Not selected") => {
  const option = options.find((item) => String(item.value) === String(value) || String(item.id) === String(value));
  return option?.label || option?.name || fallback;
};

const markRowsForSave = (rows = []) => rows.map((row) => ({
  ...row,
  row_state: row.id === undefined ? "Added" : "Updated",
}));

export const buildLeaveTypePayload = (values, options = {}) => {
  const data = { ...values, font_color: options.fontColor };

  if (options.recordId) {
    data.id = options.recordId;
  }

  if (!enabled(data.is_composite)) {
    data.composite_leave_types = [];
  }

  if (!enabled(data.is_short)) {
    data.short_leave_type_slabs = [];
  }

  if (!enabled(data.is_back_date_allowed)) {
    data.days_back_date_allowed = undefined;
  }

  if (!enabled(data.is_attachment_required)) {
    data.days_attachment_required = undefined;
  }

  if (!enabled(data.encashment)) {
    data.encashment_min = undefined;
    data.encashment_max = undefined;
  }

  if (!enabled(data.carry_forward)) {
    data.carry_forward_min = undefined;
    data.carry_forward_max = undefined;
    data.carry_forward_years = undefined;
  }

  if (!enabled(data.has_max_limit)) {
    data.limit_tenure_id = undefined;
    data.amount_apply_in_tenure = undefined;
  }

  if (enabled(data.is_from_doj)) {
    data.tax_structure_id = undefined;
  }

  if (options.includeRowState) {
    data.composite_leave_types = [
      ...(options.deletedCompositeLeaves || []),
      ...markRowsForSave(data.composite_leave_types || []),
    ];
    data.short_leave_type_slabs = [
      ...(options.deletedShortLeaves || []),
      ...markRowsForSave(data.short_leave_type_slabs || []),
    ];
  }

  const payload = {
    leave_type: data,
    pre_condition: {
      gender_id: hasValue(data.gender_id) ? data.gender_id : 0,
      can_apply_in_probation: data.can_apply_in_probation,
      min_employee_experience: data.min_employee_experience,
      max_applications_in_employment: data.max_applications_in_employment,
      gap_between_applications: data.gap_between_applications,
      min_days_per_leave: data.min_days_per_leave,
      max_days_per_leave: data.max_days_per_leave,
      can_apply_for_remaining: data.can_apply_for_remaining,
      has_max_limit: data.has_max_limit,
      limit_tenure_id: data.limit_tenure_id,
      amount_apply_in_tenure: data.amount_apply_in_tenure,
    }
  };

  if (options.recordId) {
    payload.id = options.recordId;
  }

  return payload;
};

const rangeText = (min, max, unit = "day(s)") => {
  if (hasValue(min) && hasValue(max)) {
    return `${min} to ${max} ${unit}`;
  }
  if (hasValue(min)) {
    return `At least ${min} ${unit}`;
  }
  if (hasValue(max)) {
    return `Up to ${max} ${unit}`;
  }
  return "No limit set";
};

const periodText = (start, end) => {
  if (hasValue(start) && hasValue(end)) {
    return `${start} to ${end}`;
  }
  if (hasValue(start)) {
    return `Starts ${start}`;
  }
  if (hasValue(end)) {
    return `Ends ${end}`;
  }
  return "Calculated when assigned to employees";
};

export const buildLeavePolicySummary = (payload, lookups = {}) => {
  const leaveType = payload?.leave_type || {};
  const preCondition = payload?.pre_condition || {};
  const tenureLabel = findLabel(lookups.tenures, leaveType.tenure_id);
  const genderLabel = String(preCondition.gender_id) === "0"
    ? "All employees"
    : findLabel(lookups.genders, preCondition.gender_id);
  const taxStructureLabel = findLabel(lookups.taxStructures, leaveType.tax_structure_id);
  const limitTenureLabel = findLabel(lookups.tenures, preCondition.limit_tenure_id);
  const compositeItems = (leaveType.composite_leave_types || []).filter((item) => item.row_state !== "Deleted");
  const shortLeaveItems = (leaveType.short_leave_type_slabs || []).filter((item) => item.row_state !== "Deleted");
  const allowedDays = enabled(leaveType.is_composite)
    ? "Calculated from the selected composite leave policies"
    : `${hasValue(leaveType.acc_count) ? leaveType.acc_count : 0} day(s) per ${tenureLabel}`;

  const sections = [
    {
      title: "Policy Overview",
      items: [
        { label: "Policy name", value: leaveType.name || "Not named yet" },
        { label: "Short code", value: leaveType.short_name || "Not set" },
        { label: "Status", value: enabled(leaveType.is_active) ? "Active and available for use" : "Inactive, not available for employees" },
        { label: "Auto allotment", value: enabled(leaveType.is_auto_allotment) ? "Enabled" : "Manual assignment only" },
        { label: "Period source", value: enabled(leaveType.is_from_doj) ? "Employee joining month" : taxStructureLabel },
        { label: "Quota period", value: periodText(leaveType.fiscal_year_start || leaveType.company_start, leaveType.fiscal_year_end || leaveType.company_end) },
      ],
    },
    {
      title: "Allowed Days",
      items: [
        { label: "Allowed days", value: allowedDays },
        { label: "Earned days", value: enabled(leaveType.add_earned_quota) ? "Added to employee balance" : "Not added automatically" },
        { label: "Prorated", value: enabled(leaveType.prorated) ? "Yes, days are prorated" : "No, full days are used" },
        { label: "Deductible", value: enabled(leaveType.is_deductible) ? "Requests deduct from balance" : "Requests do not deduct balance" },
      ],
    },
    {
      title: "Who Can Apply",
      items: [
        { label: "Employees", value: genderLabel },
        { label: "During probation", value: enabled(preCondition.can_apply_in_probation) ? "Allowed" : "Not allowed" },
        { label: "Minimum service", value: hasValue(preCondition.min_employee_experience) ? `${preCondition.min_employee_experience} month(s)` : "No minimum service rule" },
        { label: "Maximum applications", value: hasValue(preCondition.max_applications_in_employment) ? `${preCondition.max_applications_in_employment} per employment` : "No employment-level limit" },
        { label: "Gap between applications", value: hasValue(preCondition.gap_between_applications) ? `${preCondition.gap_between_applications} month(s)` : "No gap required" },
      ],
    },
    {
      title: "Request Rules",
      items: [
        { label: "Partial requests", value: enabled(leaveType.splitable) ? "Allowed" : "Not allowed" },
        { label: "Request duration", value: enabled(leaveType.splitable) ? rangeText(preCondition.min_days_per_leave, preCondition.max_days_per_leave) : "Employees request the configured days normally" },
        { label: "Remaining balance", value: enabled(preCondition.can_apply_for_remaining) ? "Employees may apply for remaining balance" : "Remaining balance is not separately allowed" },
        { label: "Tenure limit", value: enabled(preCondition.has_max_limit) ? `${preCondition.amount_apply_in_tenure || 0} day(s) within ${limitTenureLabel}` : "No extra tenure limit" },
        { label: "Sandwich rule", value: enabled(leaveType.sandwich) ? "Enabled" : "Not enabled" },
      ],
    },
    {
      title: "Documents And Past Dates",
      items: [
        { label: "Past-dated leave", value: enabled(leaveType.is_back_date_allowed) ? `Allowed up to ${leaveType.days_back_date_allowed || 0} day(s)` : "Not allowed" },
        { label: "Attachment", value: enabled(leaveType.is_attachment_required) ? `Required after ${leaveType.days_attachment_required || 0} day(s)` : "Not required" },
      ],
    },
    {
      title: "Unused Balance",
      items: [
        { label: "Carry forward", value: enabled(leaveType.carry_forward) ? `${rangeText(leaveType.carry_forward_min, leaveType.carry_forward_max)} for ${leaveType.carry_forward_years || 0} year(s)` : "Unused balance will not carry forward" },
        { label: "Encashment", value: enabled(leaveType.encashment) ? `Allowed for ${rangeText(leaveType.encashment_min, leaveType.encashment_max)}` : "Not allowed" },
      ],
    },
  ];

  if (enabled(leaveType.is_composite)) {
    sections.push({
      title: "Composite Leave Policies",
      items: compositeItems.length
        ? compositeItems.map((item, index) => ({
          label: `Policy ${index + 1}`,
          value: findLabel(lookups.allLeaveTypes, item.leave_type_id),
        }))
        : [{ label: "Policies", value: "No composite policies selected" }],
    });
  }

  if (enabled(leaveType.is_short)) {
    sections.push({
      title: "Short Leave Slabs",
      items: shortLeaveItems.length
        ? shortLeaveItems.map((item) => ({
          label: item.name || "Short leave slab",
          value: `${item.quota || 0} balance, ${item.type || "type not set"}, value ${item.value || 0}`,
        }))
        : [{ label: "Slabs", value: "No short leave slabs added" }],
    });
  }

  const warnings = [];
  const minDays = toNumber(preCondition.min_days_per_leave);
  const maxDays = toNumber(preCondition.max_days_per_leave);
  const carryMin = toNumber(leaveType.carry_forward_min);
  const carryMax = toNumber(leaveType.carry_forward_max);
  const encashMin = toNumber(leaveType.encashment_min);
  const encashMax = toNumber(leaveType.encashment_max);

  if (!enabled(leaveType.is_active)) {
    warnings.push("This policy is inactive, so employees will not see it until HR activates it.");
  }
  if (!enabled(leaveType.is_auto_allotment)) {
    warnings.push("Auto allotment is off. HR will need to assign this policy manually.");
  }
  if (enabled(leaveType.splitable) && minDays !== null && maxDays !== null && minDays > maxDays) {
    warnings.push("Minimum days per leave is greater than maximum days per leave.");
  }
  if (enabled(leaveType.carry_forward) && carryMin !== null && carryMax !== null && carryMin > carryMax) {
    warnings.push("Carry forward minimum is greater than carry forward maximum.");
  }
  if (enabled(leaveType.encashment) && encashMin !== null && encashMax !== null && encashMin > encashMax) {
    warnings.push("Encashment minimum is greater than encashment maximum.");
  }
  if (enabled(leaveType.is_composite) && compositeItems.length === 0) {
    warnings.push("Composite leave is enabled but no source leave policies are selected.");
  }
  if (enabled(leaveType.is_short) && shortLeaveItems.length === 0) {
    warnings.push("Short leave is enabled but no slabs are configured.");
  }

  return {
    title: `${leaveType.name || "Leave policy"} review`,
    description: "Review how this policy will behave before it is saved.",
    sections,
    warnings,
  };
};
